// DocPicker — landing view for document-mode smart pages.
//
// Lists smart documents created from a given page template, lets the
// user open one for inline editing, create a fresh document, or delete
// an existing one. Selecting a document hands control to
// `SmartPageRuntime` in document mode. The component is intentionally
// minimal — sharing/ACL UI lives elsewhere.

import { useCallback, useEffect, useState } from 'react'
import { Plus, Trash2, FileText, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  listDocuments, createDocument, deleteDocument,
  type DocumentSummary,
} from '@/api/documents'

interface Props {
  project: string
  pageObjName: string
  pageTitle: string
  onOpen: (doc: DocumentSummary) => void
}

export default function DocPicker({ project, pageObjName, pageTitle, onOpen }: Props) {
  const [docs, setDocs] = useState<DocumentSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const list = await listDocuments({ project, page_obj_name: pageObjName })
      setDocs(list)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg || 'Failed to load documents')
    }
  }, [project, pageObjName])

  useEffect(() => {
    setDocs(null)
    setError(null)
    void refresh()
  }, [refresh])

  const handleCreate = async () => {
    setCreating(true)
    try {
      const doc = await createDocument({
        project,
        page_obj_name: pageObjName,
        title: `${pageTitle} — ${new Date().toLocaleString()}`,
      })
      // Open the new document immediately so the user lands in the editor.
      onOpen(doc)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error(`Failed to create document: ${msg}`)
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (doc: DocumentSummary) => {
    if (!window.confirm(`Delete document "${doc.title}"? This cannot be undone.`)) return
    setDeletingId(doc.id)
    try {
      await deleteDocument(doc.id)
      await refresh()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error(`Failed to delete: ${msg}`)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h4 style={{
          margin: 0,
          fontSize: '0.9125rem',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}>
          Documents — {pageTitle}
        </h4>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleCreate}
          disabled={creating}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            cursor: creating ? 'wait' : 'pointer',
            opacity: creating ? 0.7 : 1,
          }}
        >
          {creating ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
          New document
        </button>
      </div>

      {error && (
        <div style={{
          padding: 12,
          border: '1px solid var(--color-error, #dc2626)',
          borderRadius: 6,
          color: 'var(--color-error, #dc2626)',
          fontSize: 14,
        }}>
          {error}
        </div>
      )}

      {docs === null && !error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: 16,
          color: 'var(--color-text-secondary)',
          fontSize: 14,
        }}>
          <Loader2 size={14} className="spin" />
          Loading documents…
        </div>
      )}

      {docs && docs.length === 0 && !error && (
        <div style={{
          padding: 24,
          textAlign: 'center',
          color: 'var(--color-text-secondary)',
          fontSize: 14,
          border: '1px dashed var(--color-border)',
          borderRadius: 6,
        }}>
          No documents yet. Click "New document" to create one.
        </div>
      )}

      {docs && docs.length > 0 && (
        <ul style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}>
          {docs.map((doc) => (
            <li
              key={doc.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 12,
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
              }}
            >
              <button
                type="button"
                onClick={() => onOpen(doc)}
                style={{
                  flex: 1,
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: 0,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                <FileText size={16} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.title || '(untitled)'}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    Updated {doc.updated_at ? new Date(doc.updated_at).toLocaleString() : '—'}
                  </span>
                </span>
              </button>
              <button
                type="button"
                aria-label="Delete"
                onClick={() => handleDelete(doc)}
                disabled={deletingId === doc.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 30,
                  height: 30,
                  borderRadius: 6,
                  background: 'transparent',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)',
                  cursor: deletingId === doc.id ? 'wait' : 'pointer',
                }}
              >
                {deletingId === doc.id ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
